'use client';
import { useMemo, useState, useEffect, useRef } from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import ExpandableText from './common/ExpandableText';
import { formatLocationLabel, getCoordsFromLocation } from '../utils/locationUtils';
import { useGeocoding } from '../common/maps/hooks/useGeocoding';
import { calculateDistance } from '../common/maps/utils/distanceCalculator';
import { calculateETA as calcETA, formatDuration } from '../common/maps/utils/distanceCalculator';
import {
  LocalShipping,
  CheckCircle,
  SwapHoriz,
  LocationOn,
  Schedule,
  Person,
  Phone,
  AccessTime,
  MyLocation,
  SupportAgent,
} from '@mui/icons-material';

/**
 * ParcelJourneyTimeline - Display multi-stage parcel journey with status transitions
 *
 * @param {Object} props
 * @param {Array} props.legs - Array of parcel legs with status, rider info, timestamps
 * @param {Object} props.parcel - Parcel object with overall status
 * @param {Object} props.liveTracking - Optional live tracking data { location, updatedAt, eta }
 */
export default function ParcelJourneyTimeline({ legs = [], parcel, liveTracking }) {
  const [lastKnownLabel, setLastKnownLabel] = useState(null);
  const [reverseLoading, setReverseLoading] = useState(false);

  const sortedLegs = useMemo(() => {
    return [...legs].sort(
      (a, b) => (a.parcelLegsLegSequence || 0) - (b.parcelLegsLegSequence || 0)
    );
  }, [legs]);

  const getStatusColor = (status) => {
    if (!status) return 'default';

    const colorMap = {
      PENDING: 'default',
      ASSIGNED: 'info',
      PICKED_UP: 'primary',
      IN_TRANSIT: 'primary',
      OUT_FOR_DELIVERY: 'warning',
      COMPLETED: 'success',
      DELIVERED: 'success',
      HANDOFF: 'warning',
    };
    return colorMap[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      PENDING: <Schedule />,
      ASSIGNED: <Person />,
      PICKED_UP: <LocalShipping />,
      IN_TRANSIT: <LocalShipping />,
      OUT_FOR_DELIVERY: <LocationOn />,
      COMPLETED: <CheckCircle />,
      DELIVERED: <CheckCircle />,
      HANDOFF: <SwapHoriz />,
    };
    return iconMap[status] || <Schedule />;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDateParts = (timestamp) => {
    if (!timestamp) return { date: 'N/A', time: '' };
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return { date: 'Invalid Date', time: '' };
      const datePart = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      const timePart = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return { date: datePart, time: timePart };
    } catch (error) {
      return { date: 'N/A', time: '' };
    }
  };

  const getAddressLabel = (address) => {
    if (!address) return null;
    if (typeof address === 'string') return address;
    if (typeof address === 'object') {
      return (
        address.fullAddress ||
        address.address ||
        address.label ||
        address.city ||
        'Address not available'
      );
    }
    return null;
  };

  // Reverse geocoding cache and helpers
  const reverseGeocodeCacheRef = useRef(new Map());
  // const [lastKnownLabel, setLastKnownLabel] = useState(null);
  // const [reverseLoading, setReverseLoading] = useState(false);
  const { reverseGeocode } = useGeocoding();

  // Get active leg (IN_TRANSIT or most recent)
  const activeLeg = useMemo(() => {
    const inTransitLeg = sortedLegs.find((leg) => leg.parcelLegsStatus === 'IN_TRANSIT');
    if (inTransitLeg) return inTransitLeg;
    return sortedLegs[sortedLegs.length - 1];
  }, [sortedLegs]);

  useEffect(() => {
    const loc = liveTracking?.location || activeLeg?.parcelLegsLastKnownLocation;
    const lat = loc?.lat ?? loc?.latitude;
    const lng = loc?.lng ?? loc?.longitude ?? loc?.lon;

    if (!lat || !lng) {
      setLastKnownLabel(null);
      setReverseLoading(false);
      return;
    }

    const key = `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
    if (reverseGeocodeCacheRef.current.has(key)) {
      setLastKnownLabel(reverseGeocodeCacheRef.current.get(key));
      setReverseLoading(false);
      return;
    }

    let mounted = true;
    setReverseLoading(true);
    (async () => {
      try {
        const address = await reverseGeocode({ lat, lng });
        if (!mounted) return;
        reverseGeocodeCacheRef.current.set(key, address || null);
        setLastKnownLabel(address || null);
      } catch (err) {
        console.error('[ParcelJourneyTimeline] reverseGeocode failed:', err);
        if (mounted) setLastKnownLabel(null);
      } finally {
        if (mounted) setReverseLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [liveTracking?.location, activeLeg?.parcelLegsLastKnownLocation, reverseGeocode]);

  // Compute fallback ETA locally if liveTracking ETA is not available
  const computedEtaFallback = useMemo(() => {
    // Prefer explicit liveTracking location if present
    const riderLoc =
      getCoordsFromLocation(liveTracking?.location) ||
      getCoordsFromLocation(activeLeg?.parcelLegsLastKnownLocation);
    // Destination: leg drop address or parcel delivery address
    const destLocRaw =
      activeLeg?.parcelLegsDropAddress || parcel?.parcelDeliveryAddress || parcel?.deliveryAddress;
    const destLoc = getCoordsFromLocation(destLocRaw);

    if (!riderLoc || !destLoc) return null;

    try {
      const distKm = calculateDistance(riderLoc, destLoc);
      const mins = calcETA(distKm);
      return mins ? formatDuration(mins) : null;
    } catch (err) {
      console.error('[ParcelJourneyTimeline] computedEtaFallback error:', err);
      return null;
    }
  }, [
    liveTracking?.location,
    activeLeg?.parcelLegsLastKnownLocation,
    activeLeg?.parcelLegsDropAddress,
    parcel,
  ]);

  if (!legs || legs.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: '#f5f5f5',
          borderRadius: 2,
          border: '1px dashed #ddd',
        }}
      >
        <LocationOn sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          No journey information available
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Journey details will appear here once the parcel is in transit
        </Typography>
      </Box>
    );
  }

  // Check if parcel is picked up
  const isPickedUp = useMemo(() => {
    return sortedLegs.some((leg) =>
      ['PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'COMPLETED', 'DELIVERED'].includes(
        leg.parcelLegsStatus
      )
    );
  }, [sortedLegs]);

  const handleSupportCall = () => {
    window.location.href = 'tel:+918000000000'; // Support number
  };

  const handleRiderCall = () => {
    if (activeLeg?.parcelLegsRiderPhone) {
      window.location.href = `tel:${activeLeg.parcelLegsRiderPhone}`;
    }
  };

  // Check if parcel is delivered
  const isDelivered = useMemo(() => {
    const status = parcel?.parcelStatus || parcel?.status;
    return status?.toUpperCase() === 'DELIVERED' || status?.toUpperCase() === 'COMPLETED';
  }, [parcel]);

  return (
    <Box sx={{ p: 0 }}>
      {/* Journey Header with Key Info */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          fontWeight={700}
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {/* Journey Details */}
          {legs.length > 1 && (
            <Chip label={`${legs.length} Legs`} size="small" color="primary" variant="outlined" />
          )}
        </Typography>

        {/* Key Information Grid - Hide for delivered parcels */}
        {!isDelivered && (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {/* Last Known Location */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: '#e3f2fd',
                borderRadius: 2,
                border: '1px solid #90caf9',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <MyLocation sx={{ fontSize: 20, color: '#1976d2', mt: 0.3 }} />
                <Box flex={1}>
                  <Typography variant="caption" fontWeight={600} color="#1976d2" display="block">
                    Last Known Location
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    {reverseLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <Box>Resolving location...</Box>
                      </Box>
                    ) : (
                      lastKnownLabel ||
                      formatLocationLabel(
                        liveTracking?.location || activeLeg?.parcelLegsLastKnownLocation
                      ) ||
                      'Tracking in progress...'
                    )}
                  </Typography>
                  {(liveTracking?.updatedAt || activeLeg?.parcelLegsUpdatedAt) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      Updated:{' '}
                      {formatDateTime(liveTracking?.updatedAt || activeLeg?.parcelLegsUpdatedAt)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>

            {/* ETA */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: '#fff3e0',
                borderRadius: 2,
                border: '1px solid #ffb74d',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTime sx={{ fontSize: 20, color: '#f57c00' }} />
                <Box flex={1}>
                  <Typography variant="caption" fontWeight={600} color="#f57c00" display="block">
                    Estimated Arrival
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: 14 }}>
                    {liveTracking?.eta ? (
                      liveTracking.eta
                    ) : liveTracking?.etaLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <Box>Calculating...</Box>
                      </Box>
                    ) : computedEtaFallback ? (
                      computedEtaFallback
                    ) : activeLeg?.parcelLegsEta ? (
                      activeLeg.parcelLegsEta
                    ) : (
                      'Calculating...'
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {/* Support Call Button */}
              <Button
                variant="contained"
                size="small"
                startIcon={<SupportAgent />}
                onClick={handleSupportCall}
                sx={{
                  bgcolor: '#000000',
                  flex: 1,
                  minWidth: 140,
                  '&:hover': {
                    bgcolor: '#45a049',
                  },
                }}
              >
                Call Support
              </Button>

              {/* Call Assigned Rider Button (if rider assigned) */}
              {activeLeg?.parcelLegsRiderPhone && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Phone />}
                  onClick={handleRiderCall}
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    flex: 1,
                    minWidth: 140,
                    '&:hover': {
                      borderColor: '#1565c0',
                      bgcolor: '#e3f2fd',
                    },
                  }}
                >
                  Call Rider
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        <Divider sx={{ mt: 2, mb: 1 }} />
      </Box>

      <Timeline
        position="right"
        sx={{ mt: 1.5, ml: -1, px: 0, width: '100%', boxSizing: 'border-box' }}
      >
        {sortedLegs.map((leg, index) => (
          <TimelineItem key={leg.parcelLegsLegId || index}>
            <TimelineOppositeContent
              color="text.secondary"
              sx={{
                flex: 0,
                width: { xs: 72, sm: 96, md: 112 },
                pt: 0.5,
                pr: 1.5,
                textAlign: 'right',
              }}
            />
            <TimelineSeparator
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* <TimelineDot color={getStatusColor(leg?.parcelLegsStatus)} variant="outlined" sx={leg.parcelLegsStatus === 'IN_TRANSIT' ? { boxShadow: '0 0 0 6px rgba(25,118,210,0.06)' } : {}}>
                {getStatusIcon(leg?.parcelLegsStatus)}
              </TimelineDot> */}
              <Box
                sx={{
                  mt: 0.5,
                  textAlign: 'center',
                  minWidth: { xs: 72, sm: 96, md: 120 },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ display: 'block', fontSize: '0.76rem' }}
                >
                  Leg {leg.parcelLegsLegSequence || index + 1}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                    lineHeight: 1.25,
                  }}
                >
                  {formatDateParts(leg.parcelLegsCreatedAt).date}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
                >
                  {formatDateParts(leg.parcelLegsCreatedAt).time}
                </Typography>
              </Box>
            </TimelineSeparator>
            <TimelineContent sx={{ py: 0.5, px: 1, flex: 1, minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid #e8eaf0',
                  bgcolor: '#fff',
                  width: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip
                      label={leg.parcelLegsStatus?.replace(/_/g, ' ') || 'Unknown'}
                      size="small"
                      color={getStatusColor(leg.parcelLegsStatus)}
                      sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    />
                    {leg.parcelLegsStatus === 'COMPLETED' ? (
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight={700}
                        sx={{ ml: 0.5 }}
                      >
                        ✓ Completed
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {leg.parcelLegsStatus?.replace(/_/g, ' ') || 'Unknown'}
                      </Typography>
                    )}
                  </Stack>

                  {leg.parcelLegsRiderName ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Person fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {leg.parcelLegsRiderName}
                      </Typography>
                      {leg.parcelLegsRiderPhone && (
                        <IconButton
                          size="small"
                          aria-label="call-rider"
                          href={`tel:${leg.parcelLegsRiderPhone}`}
                          sx={{ ml: 'auto' }}
                        >
                          <Phone sx={{ fontSize: 16, color: 'primary.main' }} />
                        </IconButton>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.disabled" fontStyle="italic">
                      No rider assigned yet
                    </Typography>
                  )}

                  {getAddressLabel(leg.parcelLegsPickupAddress) && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        bgcolor: '#e8f5e9',
                        p: 1,
                        borderRadius: 1,
                        maxHeight: '7rem',
                        overflow: 'hidden',
                      }}
                    >
                      <LocationOn sx={{ fontSize: 16, mt: 0.25, color: '#000000' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          display="block"
                          sx={{ mt: 0.25, mb: 0.25 }}
                        >
                          Pickup Location
                        </Typography>
                        <ExpandableText
                          lines={3}
                          variant="caption"
                          sx={{
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {getAddressLabel(leg.parcelLegsPickupAddress)}
                        </ExpandableText>
                      </Box>
                    </Box>
                  )}

                  {getAddressLabel(leg.parcelLegsDropAddress) && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        bgcolor: '#ffebee',
                        p: 1,
                        borderRadius: 1,
                        maxHeight: '7rem',
                        overflow: 'hidden',
                      }}
                    >
                      <LocationOn sx={{ fontSize: 16, mt: 0.25, color: '#f44336' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          display="block"
                          sx={{ mt: 0.25, mb: 0.25 }}
                        >
                          Drop Location
                        </Typography>
                        <ExpandableText
                          lines={3}
                          variant="caption"
                          sx={{
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {getAddressLabel(leg.parcelLegsDropAddress)}
                        </ExpandableText>
                      </Box>
                    </Box>
                  )}

                  <Stack spacing={0.5}>
                    {leg.parcelLegsCompletedAt && (
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="success.dark"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Schedule sx={{ fontSize: 14 }} />
                        Completed: {formatDateTime(leg.parcelLegsCompletedAt)}
                      </Typography>
                    )}
                    {leg.parcelLegsStatus === 'IN_TRANSIT' && leg.parcelLegsUpdatedAt && (
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="primary.main"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Schedule sx={{ fontSize: 14 }} />
                        Started: {formatDateTime(leg.parcelLegsUpdatedAt)}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </TimelineContent>
          </TimelineItem>
        ))}

        {parcel?.status === 'DELIVERED' && (
          <TimelineItem>
            <TimelineOppositeContent
              color="text.secondary"
              sx={{
                flex: 0,
                width: { xs: 72, sm: 96, md: 112 },
                pt: 1.5,
                pr: 1.5,
              }}
            />
            <TimelineSeparator
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* <TimelineDot color="success"><CheckCircle /></TimelineDot> */}
              <Box
                sx={{
                  mt: 0.5,
                  textAlign: 'center',
                  minWidth: { xs: 72, sm: 96, md: 120 },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ display: 'block', fontSize: '0.76rem' }}
                >
                  Final
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
                >
                  {formatDateTime(parcel.completedAt || parcel.updatedAt)}
                </Typography>
              </Box>
            </TimelineSeparator>
            <TimelineContent sx={{ py: 1.5, px: 2 }}>
              <Stack spacing={0.5}>
                <Chip label="DELIVERED" size="small" color="success" sx={{ fontWeight: 600 }} />
                <Typography variant="body2" color="success.main" fontWeight={600}>
                  ✓ Parcel Delivered Successfully
                </Typography>
                {parcel.deliveryNotes && (
                  <Typography variant="caption" color="text.secondary">
                    {parcel.deliveryNotes}
                  </Typography>
                )}
              </Stack>
            </TimelineContent>
          </TimelineItem>
        )}
      </Timeline>
    </Box>
  );
}
